import React from "react";
import { Link } from "react-router-dom";
import { 
  Zap, 
  Shield, 
  TrendingUp, 
  Award, 
  Users, 
  Factory, 
  Leaf,
  Clock,
  CheckCircle,
  Download,
  Play,
  ArrowRight,
  Star,
  Battery,
  BatteryCharging,
  Cpu,
  Globe,
  Power,
  Settings,
  Wrench,
  Sun,
  Home,
  Building,
  Cog,
  Hammer,
  WrenchIcon,
  Gauge,
  Layers,
  Box,
  Package,
  Truck,
  Recycle,
  Sparkles,
  Construction,
  Drill,
  Saw,
  FileText,
  Video,
  ChevronRight,
  Camera,
  Eye,
  Monitor,
  Wifi,
  HardDrive,
  Network,
  AlertTriangle,
  Lock,
  Fingerprint,
  Activity,
  Radar,
  CameraOff
} from "lucide-react";

import ledlightsbanner from "../img/services/Cctv/cctvbanner.jpeg";
import Cctv1 from "../img/services/Cctv/Cctv1.jpeg";
import Cctv2 from "../img/services/Cctv/Cctv2.jpeg";
import Cctv3 from "../img/services/Cctv/Cctv3.jpeg";
import Cctv4 from "../img/services/Cctv/Cctv4.jpeg";
import Cctv5 from "../img/services/Cctv/Cctv5.jpeg";
import Cctv6 from "../img/services/Cctv/Cctv6.jpeg";

function Cctv() {
  const products = [
    {
      title: "HD IP Cameras",
      description: "High-definition IP cameras for crystal clear surveillance",
      icon: Camera,
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      title: "NVR Systems",
      description: "Network video recorders for reliable storage and playback",
      icon: HardDrive,
      gradient: "from-green-500 to-emerald-600"
    },
    {
      title: "PoE Switches",
      description: "Power over Ethernet switches for efficient network connectivity",
      icon: Wifi,
      gradient: "from-orange-500 to-red-600"
    },
    {
      title: "Security Solutions",
      description: "Complete security systems for comprehensive protection",
      icon: Shield,
      gradient: "from-purple-500 to-pink-600"
    },
    {
      title: "Smart Monitoring",
      description: "AI-powered monitoring for advanced threat detection",
      icon: Eye,
      gradient: "from-teal-500 to-cyan-600"
    },
    {
      title: "Remote Access",
      description: "Mobile and web access for surveillance on the go",
      icon: Monitor,
      gradient: "from-pink-500 to-rose-600"
    }
  ];

  const features = [
    {
      title: "Advanced Technology",
      description: "Cutting-edge surveillance technology for maximum security",
      icon: Cpu,
      gradient: "from-blue-500 to-indigo-600"
    },
    {
      title: "24/7 Monitoring",
      description: "Round-the-clock surveillance for continuous protection",
      icon: Clock,
      gradient: "from-green-500 to-emerald-600"
    },
    {
      title: "High Definition",
      description: "Crystal clear video quality for detailed monitoring",
      icon: Star,
      gradient: "from-orange-500 to-red-600"
    },
    {
      title: "Easy Integration",
      description: "Seamless integration with existing security systems",
      icon: Network,
      gradient: "from-purple-500 to-pink-600"
    }
  ];

  const applications = [
    {
      title: "Industrial Security",
      description: "Comprehensive surveillance for industrial facilities",
      icon: Factory,
      file: null
    },
    {
      title: "Commercial Buildings",
      description: "Security solutions for offices and commercial spaces",
      icon: Building,
      file: null
    },
    {
      title: "Educational Institutes",
      description: "Campus security for schools and universities",
      icon: Home,
      file: null
    },
    {
      title: "Healthcare Facilities",
      description: "Advanced surveillance for hospitals and clinics",
      icon: Shield,
      file: null
    },
    {
      title: "Residential Security",
      description: "Home security systems for complete peace of mind",
      icon: Lock,
      file: null
    },
    {
      title: "Infrastructure",
      description: "Surveillance for critical infrastructure protection",
      icon: Radar,
      file: null
    }
  ];

  const galleryImages = [
    Cctv1,
    Cctv2,
    Cctv3,
    Cctv4,
    Cctv5,
    Cctv6
  ];

  return (
    <div className="home-container">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section - Rich with Colors and Effects */}
      <section className="pt-24 pb-16 px-4 sm:px-6 text-center relative overflow-hidden" style={{marginTop:'30px'}}>
        {/* Rich Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100 via-white to-indigo-100 opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Enhanced Feature Pill with Gradient */}
          <div className="inline-flex items-center gap-2 px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full text-xs sm:text-sm font-medium text-white mb-6 sm:mb-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
            <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300" />
            <span className="bg-gradient-to-r from-yellow-200 to-yellow-100 bg-clip-text text-transparent font-bold">
              Hawk's Eye
            </span>{" "}
            Security
            <Camera className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-300 animate-pulse" />
          </div>

          {/* Rich Main Headline with Multiple Gradients */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl font-extrabold tracking-tighter mb-6 sm:mb-8 leading-tight">
            <span className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 bg-clip-text text-transparent">
              CCTV
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Manufacturing
            </span>
          </h1>

          {/* Enhanced Subtext with Gradient */}
          <p className="text-lg sm:text-xl text-slate-600 mb-12 sm:mb-16 max-w-4xl mx-auto leading-relaxed bg-gradient-to-r from-slate-600 to-slate-500 bg-clip-text text-transparent px-4">
            Hawk's Eye offers a wide portfolio of products designed to meet customers' unique requirements. 
            Covers CCTV Cameras, HD IP Security Cameras, NVRs, and PoE Switches for complete security solutions.
          </p>

          {/* Rich Stats with Gradient Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto mb-16 sm:mb-20">
            <div className="group relative bg-gradient-to-br from-white to-blue-50 border border-blue-200/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-indigo-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2 sm:mb-3">
                  50+
                </div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">
                  Security Solutions
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-green-50 border border-green-200/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-emerald-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2 sm:mb-3">
                  15+
                </div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">
                  Years Experience
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-orange-50 border border-orange-200/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/10 to-red-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2 sm:mb-3">
                  4K
                </div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">
                  Ultra HD Quality
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-orange-400 to-red-600 rounded-full"></div>
            </div>

            <div className="group relative bg-gradient-to-br from-white to-purple-50 border border-purple-200/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 to-pink-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-3xl sm:text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 sm:mb-3">
                  Quick
                </div>
                <div className="text-slate-600 text-sm sm:text-base font-semibold">
                  Monitoring
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-purple-400 to-pink-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-40 h-40 sm:w-60 sm:h-60 md:w-80 md:h-80 bg-indigo-600/8 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Main container */}
          <div className="bg-gradient-to-b from-slate-900/92 to-slate-950/92 backdrop-blur-md rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 lg:p-8 xl:p-10 xl:p-12 shadow-2xl shadow-black/60">
            
            {/* Top headline area */}
            <div className="text-center mb-8 sm:mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-blue-950/60 rounded-full text-blue-300 font-semibold tracking-wider uppercase text-xs sm:text-sm mb-4 sm:mb-6">
                <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-blue-400 animate-pulse"></span>
                SECURITY MANUFACTURING
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl font-extrabold tracking-tight mb-3 sm:mb-5 leading-tight text-white">
                Discover Benefits of{" "}
                <span className="highlight-gradient">Hawk's Eye Security</span>
                {" "}by Spinoff
              </h2>

              <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed">
                Hawk's Eye brings intelligent security and surveillance solutions that protect people, 
                properties and assets. They ensure safety, security and productivity in various industrial 
                processes and help make the world safer.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 xl:gap-10">
              {/* Left column */}
              <div className="lg:col-span-12 space-y-8 lg:space-y-10 lg:space-y-12">

                {/* Manufacturing image */}
                <div className="relative rounded-xl lg:rounded-2xl overflow-hidden shadow-xl lg:shadow-2xl group">
                  <img
                    src={Cctv1}
                    alt="Advanced CCTV Manufacturing"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent flex items-end p-4 lg:p-6">
                    <div>
                      <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-2">
                        Advanced Security Solutions
                      </h3>
                      <p className="text-slate-200 text-sm lg:text-base leading-relaxed max-w-2xl">
                        Our integrated security systems have been used in various situations and they deliver 
                        comprehensive protection for people, properties and assets.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-4 lg:mb-6 text-center">
                    Why Choose Hawk's Eye Security?
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                    {features.map((feature, idx) => {
                      const Icon = feature.icon;
                      return (
                        <div
                          key={idx}
                          className="group p-4 sm:p-5 lg:p-6 rounded-xl bg-slate-800/40 hover:bg-slate-800/60 transition-all duration-300 flex flex-col items-center text-center shadow-md lg:shadow-lg"
                        >
                          <div
                            className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform shadow-lg`}
                          >
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                          </div>
                          <h4 className="font-semibold text-white text-sm sm:text-base lg:text-lg mb-2">
                            {feature.title}
                          </h4>
                          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="lg:col-span-12 space-y-8 lg:space-y-10 lg:space-y-12">

                {/* Product Categories */}
                <div>
                  <h3 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-4 lg:mb-6 text-center">
                    Our Products
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5">
                    {products.map((product, idx) => {
                      const Icon = product.icon;
                      return (
                        <div
                          key={idx}
                          className="group p-4 sm:p-5 lg:p-6 rounded-xl bg-slate-800/40 hover:bg-slate-800/60 transition-all duration-300 flex flex-col items-center text-center shadow-md lg:shadow-lg"
                        >
                          <div
                            className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br ${product.gradient} rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 transition-transform shadow-lg`}
                          >
                            <Icon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                          </div>
                          <h4 className="font-semibold text-white text-sm sm:text-base lg:text-lg mb-2">
                            {product.title}
                          </h4>
                          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
                            {product.description}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Applications Block */}
      <section
        className="brands"
        style={{ padding: "60px 0", background: "#fafafa" }}
      >
        <div className="max-w">
          <div
            className="section-head"
            style={{ marginBottom: "40px", textAlign: "center" }}
          >
            <div
              style={{
                display: "inline-flex",
                padding: "8px",
                background: "rgba(59,130,246,0.05)",
                borderRadius: "12px",
                color: "#3b82f6",
                marginBottom: "16px",
              }}
            >
              <Shield size={24} />
            </div>
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "900",
                letterSpacing: "-1px",
              }}
            >
              Security <span className="highlight-gradient">Applications.</span>
            </h2>
            <p style={{ fontSize: "16px", color: "#64748b", marginTop: "8px" }}>
              Explore our comprehensive security applications across various industries
            </p>
          </div>

          <div className="row">
            {applications.map((application, index) => (
              <div className="col-md-4" key={index}>
                <div className="icon-box">
                  <div
                    className="wt-icon-box-wraper center"
                    style={{
                      background: "white",
                      padding: "30px",
                      borderRadius: "20px",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-10px)";
                      e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.1)";
                    }}
                  >
                    <div
                      className="icon-md inline-block"
                      style={{
                        background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                        borderRadius: "15px",
                        marginBottom: "20px",
                        padding: "15px",
                      }}
                    >
                      <application.icon color="white" size={32} />
                    </div>
                    <div className="icon-content">
                      <h5
                        className="text-2xl font-bold mb-3"
                        style={{ color: "#1e293b" }}
                      >
                        {application.title}
                      </h5>
                      <p
                        className="text-gray-600 mb-4"
                        style={{ fontSize: "14px" }}
                      >
                        {application.subtitle}
                      </p>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          color: "#3b82f6",
                          fontWeight: "600",
                        }}
                      >
                        <Shield size={16} />
                        Secure Solutions
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-64 h-64 bg-blue-100/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-indigo-100/15 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-blue-600 font-semibold text-sm mb-6">
              <Camera className="w-4 h-4" />
              SECURITY GALLERY
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Our <span className="highlight-gradient">Security</span> Products
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Explore our comprehensive range of CCTV and security products
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05) translateY(-5px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1) translateY(0)";
                }}
              >
                <img
                  src={image}
                  alt={`Security Product ${index + 1}`}
                  className="w-full h-40 md:h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-2">
                  <span className="text-white text-xs font-semibold">
                    Product {index + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Secure Your Premises?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join the revolution in intelligent security solutions. Contact us today to discuss your CCTV and security requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              Get Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="https://wa.link/jb4lfe"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
            >
              Chat with Expert
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Cctv;
